import type { SupplierDocument } from '@tandemone/types'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download, Info, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useIsMobile } from '@/hooks/use-mobile'
import { formatDateTime } from '@/lib/format-date'
import { cn } from '@/lib/utils'

import { getFileIcon } from './file-icon'
import { PdfThumbnail } from './pdf-thumbnail'
import { VideoThumbnail } from './video-thumbnail'

export type DocumentDownloadResult = {
  // inline disposition. renders in <iframe> or <img>.
  previewUrl: string
  // attachment disposition. triggers a real Save As.
  downloadUrl: string
  expiresAt: string
}

/** wider category union than the shared SupplierDocument so the
 * 'certificate' label renders in the details panel. certificate isn't in
 * the shared union because the supplier-documents endpoint filters it out.
 */
export type PreviewDocument = {
  id: string
  fileName: string
  contentType: string
  category: SupplierDocument['category'] | 'certificate'
  createdAt: string
}

const CATEGORY_LABELS: Record<PreviewDocument['category'], string> = {
  facility: 'Facility',
  equipment: 'Equipment',
  product: 'Product',
  management_team: 'Management team',
  other: 'Other',
  logo: 'Logo',
  certificate: 'Certificate'
}

type Props = {
  documents: PreviewDocument[]
  // null closes the dialog. integer opens it at that index.
  initialIndex: number | null
  onClose: () => void
  fetchUrl: (documentId: string) => Promise<DocumentDownloadResult>
}

export function DocumentPreviewDialog({
  documents,
  initialIndex,
  onClose,
  fetchUrl
}: Props) {
  const open = initialIndex !== null
  const total = documents.length
  const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0)
  const [detailsOpen, setDetailsOpen] = useState(false)

  useEffect(() => {
    if (initialIndex !== null) {
      setCurrentIndex(initialIndex)
      setDetailsOpen(false)
    }
  }, [initialIndex])

  const current =
    total > 0 ? (documents[Math.min(currentIndex, total - 1)] ?? null) : null

  const goPrev = useCallback((): void => {
    if (total < 2) return
    setCurrentIndex(index => (index - 1 + total) % total)
  }, [total])
  const goNext = useCallback((): void => {
    if (total < 2) return
    setCurrentIndex(index => (index + 1) % total)
  }, [total])

  useEffect(() => {
    if (!open || total < 2) return
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, total, goNext, goPrev])

  const query = useQuery({
    queryKey: ['document-preview', current?.id],
    queryFn: () => fetchUrl(current!.id),
    enabled: open && current !== null,
    staleTime: 60_000
  })

  /* ref, not state: a re-render per touchstart restarts the pdf render. */
  const touchStart = useRef<{ x: number; y: number } | null>(null)

  function onTouchStart(event: React.TouchEvent) {
    const touch = event.touches[0]
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null
  }

  function onTouchEnd(event: React.TouchEvent) {
    const start = touchStart.current
    const touch = event.changedTouches[0]
    touchStart.current = null
    if (!start || !touch || total < 2) return
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return
    if (dx > 0) goPrev()
    else goNext()
  }

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className='flex h-[85dvh] w-full flex-col gap-0 overflow-hidden p-0 sm:h-[80vh] sm:max-w-[min(900px,90vw)]'
      >
        <header className='flex shrink-0 items-center justify-between gap-3 border-b border-border bg-page/80 px-4 py-2.5 backdrop-blur'>
          <div className='flex min-w-0 items-center gap-2'>
            <DialogTitle className='min-w-0 truncate text-sm font-medium'>
              {current?.fileName ?? 'Document'}
            </DialogTitle>
            {total > 1 ? (
              <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
                {currentIndex + 1} / {total}
              </span>
            ) : null}
          </div>
          <div className='flex shrink-0 items-center gap-0.5'>
            {query.data ? (
              <Button
                variant='ghost'
                size='icon-sm'
                asChild
                aria-label='Download document'
              >
                <a
                  href={query.data.downloadUrl}
                  target='_blank'
                  rel='noreferrer'
                  download
                >
                  <Download />
                </a>
              </Button>
            ) : null}
            <Button
              variant='ghost'
              size='icon-sm'
              onClick={() => setDetailsOpen(isOpen => !isOpen)}
              className={cn(
                'cursor-pointer',
                detailsOpen && 'bg-accent text-accent-foreground'
              )}
              aria-label={detailsOpen ? 'Hide details' : 'Show details'}
              aria-pressed={detailsOpen}
            >
              <Info />
            </Button>
            <div className='mx-1 h-5 w-px bg-border' />
            <Button
              variant='ghost'
              size='icon-sm'
              onClick={onClose}
              aria-label='Close'
              className='cursor-pointer'
            >
              <X />
            </Button>
          </div>
        </header>

        <div className='relative flex min-h-0 flex-1 overflow-hidden'>
          <div
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className='relative flex min-w-0 flex-1 items-center justify-center overflow-auto bg-muted/30'
          >
            {query.isLoading || !current ? (
              <div className='text-sm text-muted-foreground'>Loading…</div>
            ) : query.error ? (
              <div className='m-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive'>
                Failed to load document.
              </div>
            ) : query.data ? (
              /** key forces a fresh iframe / img DOM node per document so
               * the previous PDF's scroll position can't carry over.
               */
              <PreviewBody
                key={current.id}
                document={current}
                url={query.data.previewUrl}
              />
            ) : null}

            {total > 1 ? (
              <>
                <button
                  type='button'
                  onClick={goPrev}
                  aria-label='Previous document'
                  className='absolute top-1/2 left-3 grid size-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-border bg-card/90 text-foreground shadow-sm backdrop-blur transition hover:bg-card md:size-9'
                >
                  <ChevronLeft className='size-5' />
                </button>
                <button
                  type='button'
                  onClick={goNext}
                  aria-label='Next document'
                  className='absolute top-1/2 right-3 grid size-11 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-border bg-card/90 text-foreground shadow-sm backdrop-blur transition hover:bg-card md:size-9'
                >
                  <ChevronRight className='size-5' />
                </button>
              </>
            ) : null}
          </div>
          {current && detailsOpen ? (
            <DetailsPanel
              document={current}
              downloadUrl={query.data?.downloadUrl ?? null}
            />
          ) : null}
        </div>

        {total > 1 ? (
          <ThumbStrip
            documents={documents}
            currentIndex={currentIndex}
            onSelect={setCurrentIndex}
            fetchUrl={fetchUrl}
            open={open}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

/** shares the ['document-preview', id] query key with ProfileMedia's
 * carousel so urls are hot from cache when the dialog opens.
 */
function ThumbStrip({
  documents,
  currentIndex,
  onSelect,
  fetchUrl,
  open
}: {
  documents: PreviewDocument[]
  currentIndex: number
  onSelect: (index: number) => void
  fetchUrl: (documentId: string) => Promise<DocumentDownloadResult>
  open: boolean
}) {
  const previews = useQueries({
    queries: documents.map(document => ({
      queryKey: ['document-preview', document.id],
      queryFn: () => fetchUrl(document.id),
      staleTime: 60_000,
      enabled: open
    }))
  })

  return (
    <div className='flex shrink-0 gap-2 overflow-x-auto border-t border-border bg-page p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
      {documents.map((document, index) => {
        const preview = previews[index]
        const isImage = document.contentType.startsWith('image/')
        const isPdf = document.contentType === 'application/pdf'
        const isVideo = document.contentType.startsWith('video/')
        const isActive = index === currentIndex
        const { Icon, className: iconClassName } = getFileIcon(
          document.contentType
        )
        const iconFallback = (
          <div className='flex size-full flex-col items-center justify-center gap-1 p-1'>
            <Icon className={cn('size-5', iconClassName)} />
            <span className='line-clamp-2 break-all text-center text-[9px] leading-tight text-muted-foreground'>
              {document.fileName}
            </span>
          </div>
        )
        return (
          <button
            key={document.id}
            type='button'
            onClick={() => onSelect(index)}
            aria-label={`Open ${document.fileName}`}
            aria-current={isActive}
            className={cn(
              'group relative size-16 shrink-0 cursor-pointer overflow-hidden rounded-md border bg-muted transition',
              isActive
                ? 'border-foreground ring-2 ring-foreground/30'
                : 'border-border hover:border-foreground/40'
            )}
          >
            {isImage && preview?.data ? (
              <img
                src={preview.data.previewUrl}
                alt={document.fileName}
                loading='lazy'
                className='size-full object-cover'
              />
            ) : isPdf && preview?.data ? (
              <PdfThumbnail
                documentId={document.id}
                url={preview.data.previewUrl}
                fileName={document.fileName}
                targetWidth={128}
                fallback={iconFallback}
              />
            ) : isVideo && preview?.data ? (
              <VideoThumbnail
                url={preview.data.previewUrl}
                fileName={document.fileName}
              />
            ) : (
              iconFallback
            )}
          </button>
        )
      })}
    </div>
  )
}

function DetailsPanel({
  document,
  downloadUrl
}: {
  document: PreviewDocument
  downloadUrl: string | null
}) {
  return (
    /* below md a side panel leaves no room for the document, so it overlays
       the bottom of the preview instead of splitting the row. */
    <aside className='absolute inset-x-0 bottom-0 z-10 flex max-h-[60%] shrink-0 flex-col gap-3 overflow-y-auto border-t border-border bg-card p-5 md:static md:max-h-none md:w-72 md:border-t-0 md:border-l'>
      <h3 className='text-xs uppercase tracking-wide text-muted-foreground'>
        Details
      </h3>
      <dl className='flex flex-col gap-3 text-xs'>
        <Field label='Name' value={document.fileName} />
        <Field label='Type' value={formatContentType(document.contentType)} />
        <Field label='Category' value={CATEGORY_LABELS[document.category]} />
        <Field label='Added' value={formatDateTime(document.createdAt)} />
      </dl>
      {downloadUrl ? (
        <a
          href={downloadUrl}
          target='_blank'
          rel='noreferrer'
          download
          className='mt-2 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-page px-3 text-xs font-medium text-foreground hover:bg-muted md:h-auto md:py-1.5'
        >
          <Download className='size-3.5' />
          Download
        </a>
      ) : null}
    </aside>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-0.5'>
      <dt className='text-[11px] text-muted-foreground'>{label}</dt>
      <dd className='break-words text-foreground'>{value}</dd>
    </div>
  )
}

function PreviewBody({
  document,
  url
}: {
  document: PreviewDocument
  url: string
}) {
  const isMobile = useIsMobile()

  if (document.contentType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={document.fileName}
        className='size-full object-contain'
      />
    )
  }
  /* presigned GETs serve byte ranges, which is all <video> needs to seek. the
   * url expires in 5 minutes, so a long tour can lose the ability to seek
   * mid-watch -- the same limit pdfs already live with. */
  if (document.contentType.startsWith('video/')) {
    return (
      <video
        src={url}
        controls
        playsInline
        preload='metadata'
        className='size-full bg-black object-contain'
      >
        <track kind='captions' />
      </video>
    )
  }
  if (document.contentType === 'application/pdf') {
    /* ios safari renders a pdf iframe blank, so a phone gets the rendered
     * first page plus a way out to the real viewer. */
    if (isMobile) {
      return (
        <div className='flex size-full flex-col gap-3 p-4'>
          <div className='min-h-0 flex-1 overflow-y-auto rounded-md border border-border bg-white'>
            <PdfThumbnail
              documentId={document.id}
              url={url}
              fileName={document.fileName}
              targetWidth={720}
              className='h-auto! w-full object-contain'
              fallback={
                <p className='p-6 text-center text-sm text-muted-foreground'>
                  Preview not available.
                </p>
              }
            />
          </div>
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            className='inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground'
          >
            Open PDF
          </a>
        </div>
      )
    }
    /** `#page=1&zoom=auto` is a pdf-open-parameter most viewers honor.
     * without it, chrome's viewer reuses the last-seen page/zoom across
     * iframe instances on the same origin, so switching docs would
     * inherit the previous scroll position.
     */
    return (
      <iframe
        src={`${url}#page=1&zoom=auto`}
        title={document.fileName}
        className='size-full border-0 bg-white'
      />
    )
  }
  return (
    <div className='flex flex-col items-center gap-3 p-10 text-sm text-muted-foreground'>
      <p>Preview not available for {document.contentType}.</p>
      <a
        href={url}
        target='_blank'
        rel='noreferrer'
        className='inline-flex h-11 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted md:h-auto md:py-1.5'
      >
        <Download className='size-4' />
        Download
      </a>
    </div>
  )
}

function formatContentType(contentType: string): string {
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType === 'image/png') return 'PNG'
  if (contentType === 'image/jpeg') return 'JPEG'
  if (contentType.startsWith('image/')) return 'Image'
  if (contentType === 'video/mp4') return 'MP4'
  if (contentType.startsWith('video/')) return 'Video'
  return contentType
}
